import './LemonTable.scss'

import clsx from 'clsx'
import { useActions, useValues } from 'kea'
import { router } from 'kea-router'
import React, { HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IconInfo } from '@posthog/icons'
import { LemonCheckbox } from '@posthog/lemon-ui'

import { ScrollableShadows } from 'lib/components/ScrollableShadows/ScrollableShadows'
import { IconWithCount } from 'lib/lemon-ui/icons'
import { LemonButtonWithDropdown } from 'lib/lemon-ui/LemonButton'
import { More } from 'lib/lemon-ui/LemonButton/More'
import { LemonSkeleton } from 'lib/lemon-ui/LemonSkeleton'

import { useColumnWidths } from '../../hooks/useColumnWidths'
import { PaginationAuto, PaginationControl, PaginationManual, usePagination } from '../PaginationControl'
import { Tooltip } from '../Tooltip'
import { BulkSelectionBar } from './BulkSelectionBar'
import { determineColumnKey, getStickyColumnInfo } from './columnLayoutUtils'
import { LemonTableLoader } from './LemonTableLoader'
import { Sorting, SortingIndicator, getNextSorting } from './sorting'
import { TableRow } from './TableRow'
import { ExpandableConfig, LemonTableColumn, LemonTableColumnGroup, LemonTableColumns } from './types'
import { BulkSelectionConfig, BulkSelectionKey, useBulkSelection } from './useBulkSelection'

/** Sentinel passed to `useBulkSelection` when `bulkSelection` is undefined — the hook still runs
 *  unconditionally so hook order is stable, but its result is never read. */
const UNUSED_ROW_KEY = (): string | number => 0

export interface LemonTableProps<T extends Record<string, any>, K extends BulkSelectionKey = BulkSelectionKey> {
    /** Table ID that will also be used in pagination to add uniqueness to search params (page + order). */
    id?: string
    columns: LemonTableColumns<T>
    dataSource: T[]
    /** Which column to use for the row key, as an alternative to the default row index mechanism. */
    rowKey?: keyof T | ((record: T, rowIndex: number) => string | number)
    /** Class to append to each row. */
    rowClassName?: string | ((record: T, rowIndex: number) => string | null)
    /** Color to mark each row with. */
    rowRibbonColor?: string | ((record: T, rowIndex: number) => string | null | undefined)
    /** Status of each row. Defaults no status. */
    rowStatus?:
        | 'highlighted'
        | 'highlight-new'
        | ((record: T, rowIndex: number) => 'highlighted' | 'highlight-new' | null)
    /** Function that for each row determines what props should its `tr` element have based on the row's record. */
    onRow?: (record: T, index: number) => Omit<HTMLProps<HTMLTableRowElement>, 'key'>
    /** How tall should rows be. The default value is `"middle"`. */
    size?: 'small' | 'middle'
    /** Whether this table already is inset, meaning it needs reduced horizontal padding (0.5rem instead of 1rem). */
    inset?: boolean
    /** An embedded table has no border around it and no background. This way it blends better into other components. */
    embedded?: boolean
    /** Whether to hide the table background and inner borders. **/
    stealth?: boolean
    loading?: boolean
    /** Whether the table is still interactable while `loading` is `true`. Defaults to `true`. **/
    disableTableWhileLoading?: boolean
    pagination?: PaginationAuto | PaginationManual
    /**
     * Whether changing the page scrolls the table back into view. Defaults to `true`.
     * Set to `false` for tables high up on a page where paging shouldn't move the viewport.
     */
    scrollToTopOnPageChange?: boolean
    expandable?: ExpandableConfig<T>
    /** Whether the header should be shown. The default value is `true`. */
    showHeader?: boolean
    /** Whether header titles should be uppercased. The default value is `true`. */
    uppercaseHeader?: boolean
    /**
     * Table layout algorithm. Defaults to `auto` (columns size to their content). Use `fixed` to size
     * columns from the container so wide content truncates within its cell instead of overflowing the table.
     */
    tableLayout?: 'auto' | 'fixed'
    /**
     * By default sorting goes: 0. unsorted > 1. ascending > 2. descending > GOTO 0 (loop).
     * With sorting cancellation disabled, GOTO 0 is replaced by GOTO 1. */
    noSortingCancellation?: boolean
    /** Sorting order to start with. */
    defaultSorting?: Sorting | null
    /** Controlled sort order. */
    sorting?: Sorting | null
    /** Sorting change handler for controlled sort order. */
    onSort?: (newSorting: Sorting | null) => void
    /** Defaults to true. Used if you don't want to use the URL to store sort order **/
    useURLForSorting?: boolean
    /** How many skeleton rows should be used for the empty loading state. The default value is 1. */
    loadingSkeletonRows?: number
    /** What to show when there's no data. */
    emptyState?: React.ReactNode
    /** What to describe the entries as, singular and plural. The default value is `['entry', 'entries']`. */
    nouns?: [string, string]
    className?: string
    style?: React.CSSProperties
    'data-attr'?: string
    /** Footer to be shown below the table. */
    footer?: React.ReactNode
    /** Whether the first column should always remain visible when scrolling horizontally. */
    firstColumnSticky?: boolean
    /** Array of column keys to pin (make sticky). Columns won't be pinned in order. */
    pinnedColumns?: string[]
    // Max width for the column headers
    maxHeaderWidth?: string
    /** Whether to hide the scrollbar. */
    hideScrollbar?: boolean
    /**
     * Whether the table content is allowed to scroll inside its container.
     */
    allowContentScroll?: boolean
    /** Row actions to display at the end of each row. Return null to hide actions for specific rows. */
    rowActions?: (record: T, recordIndex: number) => React.ReactNode | null
    /** Whether to hide the sorting indicator when no sort is active. Defaults to false. */
    hideSortingIndicatorWhenInactive?: boolean
    /** Enable bulk-selection — adds a leading checkbox column and renders the consumer-provided
     *  action bar above the table whenever any rows are selected. */
    bulkSelection?: BulkSelectionConfig<T, K>
}

export function LemonTable<T extends Record<string, any>, K extends BulkSelectionKey = BulkSelectionKey>({
    id,
    columns: rawColumns,
    dataSource = [],
    rowKey,
    rowClassName,
    rowRibbonColor,
    rowStatus,
    onRow,
    size,
    inset = false,
    embedded = false,
    stealth = false,
    loading,
    disableTableWhileLoading = true,
    pagination,
    scrollToTopOnPageChange = true,
    expandable,
    showHeader = true,
    uppercaseHeader = true,
    tableLayout = 'auto',
    noSortingCancellation: disableSortingCancellation = false,
    defaultSorting = null,
    sorting,
    onSort,
    useURLForSorting = true,
    loadingSkeletonRows = 1,
    emptyState,
    nouns = ['entry', 'entries'],
    className,
    style,
    'data-attr': dataAttr,
    footer,
    firstColumnSticky,
    pinnedColumns,
    maxHeaderWidth,
    hideScrollbar,
    allowContentScroll = false,
    rowActions,
    hideSortingIndicatorWhenInactive = false,
    bulkSelection,
}: LemonTableProps<T, K>): JSX.Element {
    if (bulkSelection && !bulkSelection.getKey && rowKey === undefined) {
        throw new Error(
            'LemonTable `bulkSelection` requires either `bulkSelection.getKey` or a `rowKey` (string or function) to identify rows'
        )
    }
    /** Search param that will be used for storing and syncing sorting */
    const currentSortingParam = id ? `${id}_order` : 'order'

    const { location, searchParams, hashParams } = useValues(router)
    const { push } = useActions(router)

    // used when not using URL to store sorting
    const [internalSorting, setInternalSorting] = useState<Sorting | null>(sorting || null)

    /** update sorting and conditionally replace the current browsing history item */
    const setLocalSorting = useCallback(
        (newSorting: Sorting | null) => {
            setInternalSorting(newSorting)
            onSort?.(newSorting)
            if (useURLForSorting) {
                return push(
                    location.pathname,
                    {
                        ...searchParams,
                        [currentSortingParam]: newSorting
                            ? `${newSorting.order === -1 ? '-' : ''}${newSorting.columnKey}`
                            : undefined,
                    },
                    hashParams
                )
            }
        },
        [location, searchParams, hashParams, push, useURLForSorting, onSort, currentSortingParam]
    )

    const baseColumnGroups = useMemo(
        () =>
            (rawColumns.length > 0 && 'children' in rawColumns[0]
                ? rawColumns
                : [
                      {
                          children: rawColumns,
                      },
                  ]) as LemonTableColumnGroup<T>[],
        [rawColumns]
    )
    const baseColumns = useMemo(() => baseColumnGroups.flatMap((group) => group.children), [baseColumnGroups])

    const scrollRef = useRef<HTMLDivElement>(null)

    // Width calculation for pinned columns
    const { columnWidths: pinnedColumnWidths, tableRef } = useColumnWidths({
        columnKeys: pinnedColumns,
        columns: baseColumns,
    })

    /** Sorting. `useURLForSorting` gates both writing and reading the URL — when off, a stale
     * `order` param must not resurrect a sort the consumer isn't controlling. */
    const currentSorting =
        sorting ||
        internalSorting ||
        (useURLForSorting && searchParams[currentSortingParam]
            ? searchParams[currentSortingParam].startsWith('-')
                ? {
                      columnKey: searchParams[currentSortingParam].substr(1),
                      order: -1,
                  }
                : {
                      columnKey: searchParams[currentSortingParam],
                      order: 1,
                  }
            : defaultSorting)

    const sortedDataSource = useMemo(() => {
        if (currentSorting) {
            const { columnKey: sortColumnKey, order: sortOrder } = currentSorting
            const sorter = baseColumns.find(
                (searchColumn) => searchColumn.sorter && determineColumnKey(searchColumn, 'sorting') === sortColumnKey
            )?.sorter
            if (typeof sorter === 'function') {
                return dataSource.slice().sort((a, b) => sortOrder * sorter(a, b))
            }
        }
        return dataSource
    }, [dataSource, currentSorting, baseColumns])

    const paginationState = usePagination(sortedDataSource, pagination, id)

    const resolveRowKey = useMemo<(record: T) => K>(() => {
        if (bulkSelection?.getKey) {
            return bulkSelection.getKey
        }
        if (typeof rowKey === 'function') {
            return (record: T): K => rowKey(record, 0) as K
        }
        if (typeof rowKey === 'string') {
            const key = rowKey
            return (record: T): K => record[key] as K
        }
        // The constructor throws above if `bulkSelection` is set without a key source, so this
        // sentinel is only reached when `bulkSelection` is undefined and the hook's results aren't
        // consumed anyway.
        return UNUSED_ROW_KEY as unknown as (record: T) => K
    }, [bulkSelection, rowKey])

    const bulk = useBulkSelection<T, K>({
        pageRecords: paginationState.dataSourcePage,
        getKey: resolveRowKey,
        isRowSelectable: bulkSelection?.isRowSelectable,
        initialSelectedKeys: bulkSelection?.initialSelectedKeys,
    })

    const effectiveNoun = bulkSelection?.noun ?? nouns
    const selectionColumn = useMemo<LemonTableColumn<T, undefined> | null>(() => {
        if (!bulkSelection) {
            return null
        }
        return {
            key: '__bulk-selection__',
            width: 32,
            title: (
                <LemonCheckbox
                    checked={bulk.isSomeOnPageSelected ? 'indeterminate' : bulk.isAllOnPageSelected}
                    onChange={bulk.toggleAllOnPage}
                    aria-label={bulkSelection.headerAriaLabel ?? `Select all ${effectiveNoun[1]} on this page`}
                    disabledReason={
                        !bulk.pageHasSelectableRows ? `No ${effectiveNoun[1]} on this page can be selected` : null
                    }
                />
            ),
            render: function RenderBulkSelectionCell(_, record: T, recordIndex: number) {
                const key = resolveRowKey(record)
                const pageIndex = recordIndex - paginationState.currentStartIndex
                const gate = bulkSelection.isRowSelectable ? bulkSelection.isRowSelectable(record, pageIndex) : true
                const disabledReason: string | null =
                    gate === false
                        ? 'Selection disabled'
                        : typeof gate === 'object' && gate !== null
                          ? gate.disabledReason
                          : null
                return (
                    <LemonCheckbox
                        checked={bulk.selectedKeysSet.has(key)}
                        onChange={(_value, event) =>
                            bulk.toggleRow(key, pageIndex, (event.nativeEvent as MouseEvent).shiftKey ?? false)
                        }
                        disabledReason={disabledReason}
                        aria-label={bulkSelection.rowAriaLabel?.(record)}
                    />
                )
            },
        }
    }, [bulkSelection, bulk, resolveRowKey, paginationState.currentStartIndex, effectiveNoun])

    const columnGroups = useMemo<LemonTableColumnGroup<T>[]>(() => {
        if (!selectionColumn) {
            return baseColumnGroups
        }
        return baseColumnGroups.map((group, index) =>
            index === 0
                ? {
                      ...group,
                      children: [selectionColumn as LemonTableColumn<T, keyof T | undefined>, ...group.children],
                  }
                : group
        )
    }, [baseColumnGroups, selectionColumn])

    const columns = useMemo(() => columnGroups.flatMap((group) => group.children), [columnGroups])
    const previousPageRef = useRef<number | null>(null)

    useEffect(() => {
        // Don't auto-scroll on initial mount
        if (previousPageRef.current === null) {
            previousPageRef.current = paginationState.currentPage
            return
        }
        if (previousPageRef.current === paginationState.currentPage) {
            return
        }
        previousPageRef.current = paginationState.currentPage

        if (!scrollToTopOnPageChange) {
            return
        }

        // When the current page changes, scroll back to the top of the table
        if (scrollRef.current) {
            const realTableOffsetTop = scrollRef.current.getBoundingClientRect().top - 320 // Extra breathing room
            // If the table starts above the top edge of the view, scroll to the top of the table minus breathing room
            if (realTableOffsetTop < 0) {
                const scrollContainer = document.querySelector('main') || window
                if (scrollContainer === window) {
                    window.scrollTo(window.scrollX, window.scrollY + realTableOffsetTop)
                } else {
                    scrollContainer.scrollBy(0, realTableOffsetTop)
                }
            }
        }
    }, [paginationState.currentPage, scrollToTopOnPageChange])

    if (firstColumnSticky && expandable) {
        // Due to CSS, for firstColumnSticky to work the first column needs to be a content column
        throw new Error('LemonTable `firstColumnSticky` prop cannot be used with `expandable`')
    }

    const isRowExpansionToggleShown = expandable ? (expandable?.showRowExpansionToggle ?? true) : false

    const visibleDataColumnCount = useMemo(() => columns.filter((column) => !column.isHidden).length, [columns])
    // Matches the main header row cell count so the loader row does not add an extra table column (which shifts headers while loading)
    const headerLoaderColSpan = Math.max(
        1,
        Number(isRowExpansionToggleShown) + visibleDataColumnCount + Number(!!rowActions)
    )

    return (
        <>
            {bulkSelection && <BulkSelectionBar context={bulk.context} config={bulkSelection} noun={effectiveNoun} />}
            <div
                id={id}
                className={clsx(
                    'LemonTable',
                    size && size !== 'middle' && `LemonTable--${size}`,
                    inset && 'LemonTable--inset',
                    loading && disableTableWhileLoading && 'LemonTable--loading',
                    embedded && 'LemonTable--embedded',
                    rowRibbonColor !== undefined && `LemonTable--with-ribbon`,
                    stealth && 'LemonTable--stealth',
                    !uppercaseHeader && 'LemonTable--lowercase-header',
                    allowContentScroll && 'h-full min-h-0 overflow-hidden',
                    className
                )}
                // eslint-disable-next-line react/forbid-dom-props
                style={style}
                data-attr={dataAttr}
            >
                <ScrollableShadows
                    innerClassName={hideScrollbar ? 'hide-scrollbar' : undefined}
                    direction={allowContentScroll ? undefined : 'horizontal'}
                    scrollRef={scrollRef}
                >
                    <div className="LemonTable__content">
                        <table ref={tableRef} className={tableLayout === 'fixed' ? 'table-fixed' : undefined}>
                            <colgroup>
                                {
                                    isRowExpansionToggleShown && (
                                        <col style={{ width: '1%' }} />
                                    ) /* Expand/collapse column */
                                }
                                {columns
                                    .filter((column) => !column.isHidden)
                                    .map((column, index) => (
                                        // eslint-disable-next-line react/forbid-dom-props
                                        <col
                                            key={`LemonTable-col-${index}`}
                                            // width:0 has no effect in auto-layout tables (ignored by Safari).
                                            // width:1% is a standard workaround to shrink a column to its content.
                                            style={{ width: column.width === 0 ? '1%' : column.width }}
                                        />
                                    ))}
                            </colgroup>
                            {showHeader && (
                                <thead>
                                    {columnGroups.some((group) => group.title) && (
                                        <tr className="LemonTable__row--grouping">
                                            {
                                                isRowExpansionToggleShown && (
                                                    <th className="LemonTable__toggle" />
                                                ) /* Expand/collapse */
                                            }
                                            {columnGroups.map((columnGroup, columnGroupIndex) =>
                                                columnGroupIndex === 0 && firstColumnSticky ? (
                                                    <React.Fragment key={`LemonTable-th-group-${columnGroupIndex}`}>
                                                        <th
                                                            colSpan={1}
                                                            className="LemonTable__boundary LemonTable__header--sticky"
                                                        >
                                                            {columnGroup.title}
                                                        </th>
                                                        {/* The DOM clamps colSpan 0 up to 1, so a single-child
                                                            group must not render the filler at all: the phantom
                                                            column shifts every group title after it one column
                                                            to the right. */}
                                                        {columnGroup.children.length > 1 && (
                                                            <th colSpan={columnGroup.children.length - 1} />
                                                        )}
                                                    </React.Fragment>
                                                ) : (
                                                    <th
                                                        key={`LemonTable-th-group-${columnGroupIndex}`}
                                                        colSpan={columnGroup.children.length}
                                                        className="LemonTable__boundary"
                                                    >
                                                        {columnGroup.title}
                                                    </th>
                                                )
                                            )}
                                        </tr>
                                    )}
                                    <tr>
                                        {
                                            isRowExpansionToggleShown && (
                                                <th className="LemonTable__toggle" />
                                            ) /* Expand/collapse */
                                        }
                                        {columnGroups.flatMap((columnGroup, columnGroupIndex) =>
                                            columnGroup.children
                                                .filter((column) => !column.isHidden)
                                                .map((column, columnIndex) => {
                                                    const columnKey = determineColumnKey(column) ?? `${columnIndex}`
                                                    const stickyInfo = getStickyColumnInfo(
                                                        columnKey,
                                                        pinnedColumns,
                                                        pinnedColumnWidths,
                                                        columns
                                                    )
                                                    const { isSticky: isPinned, leftPosition } = stickyInfo

                                                    // Truncate only when a max width is set and the column isn't sized by its author.
                                                    const truncateHeader =
                                                        !!maxHeaderWidth && !column.width && !column.fullWidth

                                                    return (
                                                        <th
                                                            key={`LemonTable-th-${columnGroupIndex}-${columnKey}`}
                                                            className={clsx(
                                                                'LemonTable__header',
                                                                column.sorter && 'LemonTable__header--actionable',
                                                                columnIndex === 0 && 'LemonTable__boundary',
                                                                firstColumnSticky &&
                                                                    columnGroupIndex === 0 &&
                                                                    columnIndex === 0 &&
                                                                    'LemonTable__header--sticky',
                                                                isPinned && 'LemonTable__header--pinned',
                                                                column.className
                                                            )}
                                                            /* eslint-disable-next-line react/forbid-dom-props */
                                                            style={{
                                                                textAlign: column.align,
                                                                ...(isPinned ? { left: `${leftPosition}px` } : {}),
                                                            }}
                                                        >
                                                            <div
                                                                className="LemonTable__header-content"
                                                                /* eslint-disable-next-line react/forbid-dom-props */
                                                                style={{
                                                                    justifyContent:
                                                                        column.align === 'center'
                                                                            ? 'center'
                                                                            : column.align === 'right'
                                                                              ? 'flex-end'
                                                                              : 'flex-start',
                                                                }}
                                                                onClick={
                                                                    column.sorter
                                                                        ? (event) => {
                                                                              const target = event.target as HTMLElement

                                                                              // Check if the click happened on the checkbox input, label, or its specific SVG (LemonCheckbox__box)
                                                                              if (
                                                                                  target.closest('.LemonCheckbox') ||
                                                                                  target.classList.contains(
                                                                                      'LemonCheckbox__box'
                                                                                  ) ||
                                                                                  target.tagName.toLowerCase() ===
                                                                                      'label' ||
                                                                                  target.tagName.toLowerCase() ===
                                                                                      'input' ||
                                                                                  target.closest(
                                                                                      '[data-attr="table-header-more"]'
                                                                                  )
                                                                              ) {
                                                                                  return // Do nothing if the click is on the checkbox or more button
                                                                              }

                                                                              const nextSorting = getNextSorting(
                                                                                  currentSorting,
                                                                                  determineColumnKey(column, 'sorting'),
                                                                                  disableSortingCancellation,
                                                                                  column.defaultSortOrder
                                                                              )

                                                                              setLocalSorting(nextSorting)
                                                                          }
                                                                        : undefined
                                                                }
                                                            >
                                                                <div
                                                                    className={clsx(
                                                                        'flex items-center',
                                                                        // Clip at maxWidth: sticky headers keep `overflow: visible` on the th, so
                                                                        // without this an over-wide title spills across the neighbouring headers
                                                                        truncateHeader && 'overflow-hidden',
                                                                        column?.fullWidth && 'w-full',
                                                                        column.sorter && 'cursor-pointer'
                                                                    )}
                                                                    /* eslint-disable-next-line react/forbid-dom-props */
                                                                    style={
                                                                        truncateHeader
                                                                            ? { maxWidth: maxHeaderWidth }
                                                                            : undefined
                                                                    }
                                                                >
                                                                    {column.tooltip ? (
                                                                        <Tooltip title={column.tooltip}>
                                                                            <div className="flex items-center">
                                                                                {column.title}
                                                                                <IconInfo className="ml-1 text-base" />
                                                                            </div>
                                                                        </Tooltip>
                                                                    ) : truncateHeader &&
                                                                      typeof column.title === 'string' ? (
                                                                        <div
                                                                            className="min-w-0 truncate"
                                                                            title={column.title}
                                                                        >
                                                                            {column.title}
                                                                        </div>
                                                                    ) : (
                                                                        column.title
                                                                    )}
                                                                    {column.sorter &&
                                                                        (() => {
                                                                            const columnKey = determineColumnKey(
                                                                                column,
                                                                                'sorting'
                                                                            )
                                                                            const isActiveSort =
                                                                                currentSorting?.columnKey === columnKey
                                                                            const order = isActiveSort
                                                                                ? currentSorting.order
                                                                                : null

                                                                            // Hide indicator if inactive and hideSortingIndicatorWhenInactive is true
                                                                            if (
                                                                                hideSortingIndicatorWhenInactive &&
                                                                                !isActiveSort
                                                                            ) {
                                                                                return null
                                                                            }

                                                                            return (
                                                                                <Tooltip
                                                                                    title={() => {
                                                                                        const nextSorting =
                                                                                            getNextSorting(
                                                                                                currentSorting,
                                                                                                columnKey,
                                                                                                disableSortingCancellation,
                                                                                                column.defaultSortOrder
                                                                                            )
                                                                                        return `Click to ${
                                                                                            nextSorting
                                                                                                ? nextSorting.order ===
                                                                                                  1
                                                                                                    ? 'sort ascending'
                                                                                                    : 'sort descending'
                                                                                                : 'cancel sorting'
                                                                                        }`
                                                                                    }}
                                                                                >
                                                                                    <SortingIndicator order={order} />
                                                                                </Tooltip>
                                                                            )
                                                                        })()}
                                                                </div>
                                                                {column.more &&
                                                                    (column.moreIcon ? (
                                                                        <LemonButtonWithDropdown
                                                                            aria-label="more"
                                                                            data-attr="table-header-more"
                                                                            icon={
                                                                                column.moreFilterCount !== undefined &&
                                                                                column.moreFilterCount > 0 ? (
                                                                                    <IconWithCount
                                                                                        count={column.moreFilterCount}
                                                                                        showZero={false}
                                                                                        status="danger"
                                                                                    >
                                                                                        {column.moreIcon}
                                                                                    </IconWithCount>
                                                                                ) : (
                                                                                    column.moreIcon
                                                                                )
                                                                            }
                                                                            dropdown={{
                                                                                placement: 'bottom-end',
                                                                                actionable: true,
                                                                                overlay: column.more,
                                                                            }}
                                                                            size="small"
                                                                            className="ml-1"
                                                                        />
                                                                    ) : (
                                                                        <More
                                                                            overlay={column.more}
                                                                            className="ml-1"
                                                                            data-attr="table-header-more"
                                                                        />
                                                                    ))}
                                                            </div>
                                                        </th>
                                                    )
                                                })
                                        )}
                                        {rowActions && <th className="w-0" />}
                                    </tr>
                                    <tr className="LemonTable__loader-row">
                                        <th colSpan={headerLoaderColSpan} className="LemonTable__loader-host">
                                            <LemonTableLoader loading={loading} tag="div" />
                                        </th>
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {paginationState.dataSourcePage.length ? (
                                    paginationState.dataSourcePage.map((record, rowIndex) => {
                                        const rowKeyDetermined = rowKey
                                            ? typeof rowKey === 'function'
                                                ? rowKey(record, rowIndex)
                                                : (record[rowKey] ?? rowIndex)
                                            : paginationState.currentStartIndex + rowIndex
                                        const rowClassNameDetermined =
                                            typeof rowClassName === 'function'
                                                ? rowClassName(record, rowIndex)
                                                : rowClassName
                                        const rowRibbonColorDetermined =
                                            typeof rowRibbonColor === 'function'
                                                ? rowRibbonColor(record, rowIndex) || 'var(--color-border-primary)'
                                                : rowRibbonColor
                                        const rowStatusDetermined =
                                            typeof rowStatus === 'function' ? rowStatus(record, rowIndex) : rowStatus

                                        return (
                                            <TableRow
                                                key={`LemonTable-tr-${rowKeyDetermined}`}
                                                record={record}
                                                recordIndex={paginationState.currentStartIndex + rowIndex}
                                                rowKeyDetermined={rowKeyDetermined}
                                                rowClassNameDetermined={rowClassNameDetermined}
                                                rowRibbonColorDetermined={rowRibbonColorDetermined}
                                                rowStatusDetermined={rowStatusDetermined}
                                                columnGroups={columnGroups}
                                                onRow={onRow}
                                                expandable={expandable}
                                                rowCount={paginationState.dataSourcePage.length}
                                                firstColumnSticky={firstColumnSticky}
                                                pinnedColumns={pinnedColumns}
                                                pinnedColumnWidths={pinnedColumnWidths}
                                                columns={columns}
                                                rowActions={rowActions}
                                            />
                                        )
                                    })
                                ) : loading ? (
                                    Array(loadingSkeletonRows)
                                        .fill(null)
                                        .map((_, rowIndex) => (
                                            <tr key={`LemonTable-tr-${rowIndex} ph-no-capture`}>
                                                {columnGroups.flatMap((columnGroup, columnGroupIndex) =>
                                                    columnGroup.children.map((column, columnIndex) => (
                                                        <td
                                                            key={`LemonTable-td-${columnGroupIndex}-${columnIndex}`}
                                                            className={clsx(
                                                                columnIndex === columnGroup.children.length - 1 &&
                                                                    'LemonTable__boundary',
                                                                firstColumnSticky &&
                                                                    columnIndex === 0 &&
                                                                    'LemonTable__cell--sticky',
                                                                column.className
                                                            )}
                                                        >
                                                            <LemonSkeleton />
                                                        </td>
                                                    ))
                                                )}
                                            </tr>
                                        ))
                                ) : (
                                    <tr className="LemonTable__empty-state">
                                        <td colSpan={headerLoaderColSpan}>{emptyState || `No ${nouns[1]}`}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {footer && <div className="LemonTable__footer">{footer}</div>}

                        <PaginationControl {...paginationState} nouns={nouns} />
                        <div className="LemonTable__overlay" />
                    </div>
                </ScrollableShadows>
            </div>
        </>
    )
}
